import React, { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Row, Col, Form } from 'react-bootstrap';
import classNames from 'classnames';

import { requestList as request, requestDelete } from 'api/board/rawMaterial';
import { requestList as requestCode } from 'api/standard/code';
import ActionButton from 'components/common/ActionButton';
import Select from 'components/common-sfims/form/Select';
import ListInContent from 'components/common-sfims/ListInContent';
import Pagination from 'components/common-sfims/pagination/paging';
import columns from 'components/pages/board/rawMaterial/columns';
import { array2ValueAndText, utc2tz } from 'function/convert';
import { onListClick } from 'function/event';
import { getText } from 'function/language';
import { getLoginInform } from 'function/login';
import { getErrorMessage } from 'function/message';
import { useConfirmModalState } from 'global/confirmModalState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE, ROUTER_PATH } from 'types/const';
import { RETURN_YN } from 'types/type';
import comma from 'utils/format/comma';
import format from 'utils/string/format';

/**
 * 협업 게시판 > 신규 원료 코드 신청 - 목록
 */
const List = () => {
  /* variable */
  const { adminYn } = getLoginInform();
  const size = 15;

  /* router */
  const history = useHistory();
  const { state } = useLocation();

  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 메시지
  const [message, setMessage] = useState({});
  // 선택한 하위 분류
  const [mtrilGrpCd, setMtrilGrpCd] = useState(state?.mtrilGrpCd);
  // 하위 분류 목록
  const [mtrilGrpCds, setMtrilGrpCds] = useState([]);
  // 페이지
  const [page, setPage] = useState(1);
  // 신규 원료 코드 신청 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer(state);
  // 원료 코드 검색어
  const [rwmtrCd, setRwmtrCd] = useState(state?.rwmtrCd ?? '');
  // 원료 명 검색어
  const [rwmtrNm, setRwmtrNm] = useState(state?.rwmtrNm ?? '');
  // 총 데이터 수
  const [total, setTotal] = useState(0);

  /* watch */
  useEffect(() => {
    requestMtrilGrpCd();
  }, []);
  useEffect(() => {
    requestList();
  }, [params]);

  /* method */
  // 신규 원료 코드 신청 목록 조회 요청
  const requestList = async () => {
    setDatas([]);
    setIsLoading(true);
    setMessage({});
    setTotal(0);

    try {
      const { list = [], code, message } = await request(params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      const { length } = list;

      setDatas(
        list.map((value = {}, index) => ({
          ...value,
          rowNum: length - size * (page - 1) - index,
          regDt: utc2tz(value.regDt),
          comptDt: utc2tz(value.comptDt)
        }))
      );
      setTotal(length);
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 하위 분류 목록 조회 요청
  const requestMtrilGrpCd = async () => {
    const { list = [] } = await requestCode({ codeGroup: 'rwmtr_subclass' });

    setMtrilGrpCds(list);
  };

  /* event */
  // 목록 클릭 시
  const onClick = seq => {
    history.push(format(ROUTER_PATH.board.rawMaterial.inform, { seq }), params);
  };
  // 삭제 클릭 시
  const onDeleteClick = seq => {
    setConfirmModal({
      isOpen: true,
      title: 'delete',
      message: 'deleteMessage',
      isCancelBtn: true,
      btnNm: 'delete',
      async onConfirm() {
        try {
          const { code, message } = await requestDelete(seq);

          if (code !== RESPONSE_CODE.Success) {
            throw {
              code,
              error: message
            };
          }

          // 신규 원료 코드 신청 목록 조회 요청
          await requestList();
        } catch (error) {
          setConfirmModal({
            isOpen: true,
            message: getErrorMessage(error?.code, error?.error)
          });
        }
      }
    });
  };
  // 검색 클릭 시
  const onSearchClick = () => {
    setParams({
      rwmtrCd,
      rwmtrNm,
      mtrilGrpCd
    });
  };
  // 등록/수정 클릭 시
  const onSettingClick = seq => {
    history.push(
      format(ROUTER_PATH.board.rawMaterial.setting, { seq }),
      params
    );
  };

  /* view */
  return (
    <ListInContent
      columns={Object.entries(columns).map(
        ([key, { title } = {}]) => title ?? key
      )}
      message={message}
      showLoading={isLoading}
    >
      <Fragment key="headerButton">
        <Button size="sm" className="ms-2" onClick={() => onSettingClick()}>
          {getText(
            adminYn === RETURN_YN.Y
              ? 'createRawMaterial'
              : 'insertRawMaterialButton'
          )}
        </Button>
      </Fragment>
      <Fragment key="search">
        <Form onSubmit={onSearchClick}>
          <Row className="ms-0 me-0 mt-3">
            <Col sm={2} className="ps-1 pe-1">
              <Form.Control
                size="sm"
                type="text"
                value={rwmtrCd}
                autoFocus
                placeholder={getText('rwmtrCd')}
                onChange={({ target: { value } }) => setRwmtrCd(value)}
              />
            </Col>
            <Col sm={2} className="ps-1 pe-1">
              <Form.Control
                size="sm"
                type="text"
                value={rwmtrNm}
                placeholder={getText('rwmtrNm')}
                onChange={({ target: { value } }) => setRwmtrNm(value)}
              />
            </Col>
            <Col sm={2} className="ps-1 pe-1">
              <Select
                datas={array2ValueAndText(mtrilGrpCds, 'codeCd', 'codeNmKr')}
                placeholder="rawMaterialCtgryNmKr"
                showAll
                value={mtrilGrpCd}
                className="w-100"
                onChange={({ target: { value } }) => setMtrilGrpCd(value)}
              />
            </Col>
            <Col
              sm="auto"
              className="ps-1 pe-1 d-flex flex-row align-items-center"
            >
              <Button disabled={isLoading} size="sm" type="submit">
                {getText('search')}
              </Button>
              <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                {format(getText('total'), { total: comma(total) })}
              </p>
            </Col>
          </Row>
        </Form>
      </Fragment>
      <Fragment key="datas">
        {datas.slice(size * (page - 1), size * page).map((value = {}) => (
          <tr
            key={`datas${value.seq}`}
            onClick={event => onListClick(event, () => onClick(value.seq))}
          >
            {Object.keys(columns).map(key => (
              <td
                key={`datas${value.seq}.${key}`}
                className={classNames({
                  'cursor-pointer': key !== 'updateOrDelete',
                  'text-danger': key === 'sttusNm' && value.sttus === 1,
                  'text-primary': key === 'sttusNm' && value.sttus === 2,
                  'text-success': key === 'sttusNm' && value.sttus === 3
                })}
              >
                {key === 'updateOrDelete'
                  ? (adminYn === RETURN_YN.Y ||
                      value.userFlag === RETURN_YN.Y) &&
                    value.sttus === 0 && (
                      <>
                        <ActionButton
                          title={getText('update')}
                          icon="edit"
                          variant="action"
                          className="p-0 lh-1"
                          onClick={() => onSettingClick(value.seq)}
                        />
                        <ActionButton
                          title={getText('delete')}
                          icon="trash-alt"
                          variant="action"
                          className="p-0 lh-1 ms-2"
                          onClick={() => onDeleteClick(value.seq)}
                        />
                      </>
                    )
                  : value[key]}
              </td>
            ))}
          </tr>
        ))}
      </Fragment>
      <Fragment key="pagination">
        {total > 0 && (
          <Pagination
            currentPage={page}
            perPage={size}
            length={10}
            totalCount={total}
            movePage={page => setPage(page)}
          />
        )}
      </Fragment>
    </ListInContent>
  );
};

export default List;
